import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, Mail, MessageSquare, Check, AlertCircle, Send } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function NotificationSettings({ user, token, onUpdate }) {
  const { refreshUser } = useAuth();
  const [notificationMethod, setNotificationMethod] = useState(user?.notification_method || "ringcentral");
  const [ringcentralUsername, setRingcentralUsername] = useState(user?.ringcentral_username || user?.email || "");
  const [notificationEmail, setNotificationEmail] = useState(user?.notification_email || user?.email || "");
  const [smsFormat, setSmsFormat] = useState(user?.sms_format || "detailed");
  const [receiveFailureNotifications, setReceiveFailureNotifications] = useState(user?.receive_failure_notifications || false);
  const [isVerified, setIsVerified] = useState(user?.notification_verified || false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const isAdmin = user?.roles?.includes("admin");

  // Detect unsaved changes so Verify can auto-save first.
  const isDirty =
    notificationMethod !== (user?.notification_method || "ringcentral") ||
    smsFormat !== (user?.sms_format || "detailed") ||
    (notificationMethod === "ringcentral" &&
      ringcentralUsername !== (user?.ringcentral_username || user?.email || "")) ||
    (notificationMethod === "email" &&
      notificationEmail !== (user?.notification_email || user?.email || ""));

  useEffect(() => {
    if (user) {
      setNotificationMethod(user.notification_method || "ringcentral");
      setRingcentralUsername(user.ringcentral_username || user.email || "");
      setNotificationEmail(user.notification_email || user.email || "");
      setSmsFormat(user.sms_format || "detailed");
      setReceiveFailureNotifications(user.receive_failure_notifications || false);
      setIsVerified(user.notification_verified || false);
    }
  }, [user]);

  const handleSavePreferences = async () => {
    try {
      setSaving(true);

      // Validate email if email method is selected
      if (notificationMethod === "email" && !notificationEmail.toLowerCase().endsWith("@therapymgmt.com")) {
        toast.error("Email address must end with @therapymgmt.com");
        return;
      }

      const resp = await axios.put(
        `${API}/users/me/notification-preferences`,
        {
          notification_method: notificationMethod,
          ringcentral_username: notificationMethod === "ringcentral" ? ringcentralUsername : null,
          notification_email: notificationMethod === "email" ? notificationEmail : null,
          sms_format: smsFormat,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Refresh the AuthContext user so the rest of the app (and the next
      // time this dialog is opened) sees the new values from the DB instead
      // of the original cached user object from login.
      await refreshUser();

      if (resp.data?.needs_verification) {
        setIsVerified(false);
        toast.success("Notification preferences saved! Please verify your settings.");
      } else {
        toast.success("Notification preferences saved.");
      }
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAdminNotifications = async () => {
    try {
      await axios.put(
        `${API}/users/me/admin-notifications`,
        { receive_failure_notifications: receiveFailureNotifications },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Admin notification settings saved!");
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save admin settings");
    }
  };

  const handleTestNotification = async () => {
    try {
      setTesting(true);

      // If the user changed settings without saving first, the verify call
      // would test the OLD saved settings — which is confusing. Auto-save
      // pending changes so the user always tests what they actually see.
      if (isDirty) {
        if (notificationMethod === "email" && !notificationEmail.toLowerCase().endsWith("@therapymgmt.com")) {
          toast.error("Email address must end with @therapymgmt.com");
          return;
        }
        await axios.put(
          `${API}/users/me/notification-preferences`,
          {
            notification_method: notificationMethod,
            ringcentral_username: notificationMethod === "ringcentral" ? ringcentralUsername : null,
            notification_email: notificationMethod === "email" ? notificationEmail : null,
            sms_format: smsFormat,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        await refreshUser();
      }

      const response = await axios.post(
        `${API}/users/me/verify-notification`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.verified) {
        const refreshed = await refreshUser();
        setIsVerified(refreshed?.notification_verified ?? true);
        toast.success("Test notification sent successfully! Your settings are verified.");
        if (onUpdate) onUpdate();
      } else {
        const reason = response.data.message || response.data.error || "Test notification failed";
        toast.error(reason, { duration: 7000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test notification");
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="w-5 h-5" />
          Notification Settings
        </CardTitle>
        <CardDescription>
          Choose how you want to receive notifications from TMC TeleTech
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Notification Method */}
        <div className="space-y-3">
          <Label>Notification Method</Label>
          <Select value={notificationMethod} onValueChange={setNotificationMethod}>
            <SelectTrigger className="w-full" data-testid="notification-method-select">
              <SelectValue placeholder="Select notification method" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ringcentral">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  RingCentral Direct Message
                </div>
              </SelectItem>
              <SelectItem value="email">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* RingCentral Settings */}
        {notificationMethod === "ringcentral" && (
          <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <Label htmlFor="ringcentral_username">RingCentral Username</Label>
            <Input
              id="ringcentral_username"
              value={ringcentralUsername}
              onChange={(e) => setRingcentralUsername(e.target.value)}
              placeholder="your.email@therapymgmt.com"
              data-testid="ringcentral-username-input"
            />
            <p className="text-xs text-muted-foreground">
              This should match your RingCentral login (usually your work email)
            </p>

            {/* SMS Format Preference */}
            <div className="pt-3 mt-3 border-t border-blue-200 space-y-2">
              <Label htmlFor="sms_format">SMS Message Format</Label>
              <Select value={smsFormat} onValueChange={setSmsFormat}>
                <SelectTrigger id="sms_format" className="w-full bg-white" data-testid="sms-format-select">
                  <SelectValue placeholder="Select SMS format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="detailed" data-testid="sms-format-detailed">
                    <div className="flex flex-col text-left">
                      <span className="font-medium">Detailed (default)</span>
                      <span className="text-xs text-muted-foreground">
                        Full context — patient, time, contact info, notes, etc.
                      </span>
                    </div>
                  </SelectItem>
                  <SelectItem value="concise" data-testid="sms-format-concise">
                    <div className="flex flex-col text-left">
                      <span className="font-medium">Concise (1 line)</span>
                      <span className="text-xs text-muted-foreground">
                        Compact single-line summary — great for high volumes
                      </span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Applies to booking, cancellation, patient-ready, and waitlist SMS.
                Email notifications always stay in full detail.
              </p>
            </div>
          </div>
        )}

        {/* Email Settings */}
        {notificationMethod === "email" && (
          <div className="space-y-3 p-4 bg-green-50 rounded-lg border border-green-200">
            <Label htmlFor="notification_email">Notification Email</Label>
            <Input
              id="notification_email"
              type="email"
              value={notificationEmail}
              onChange={(e) => setNotificationEmail(e.target.value)}
              placeholder="your.email@therapymgmt.com"
              data-testid="notification-email-input"
            />
            <p className="text-xs text-muted-foreground">
              <AlertCircle className="w-3 h-3 inline mr-1" />
              Email must end with @therapymgmt.com (TMC emails only)
            </p>
          </div>
        )}

        {/* Verification Status */}
        <div className={`flex items-center justify-between p-3 rounded-lg ${isVerified ? "bg-green-50 border border-green-200" : "bg-amber-50 border border-amber-200"}`}>
          <div className="flex items-center gap-2">
            {isVerified ? (
              <>
                <Check className="w-5 h-5 text-green-600" />
                <div>
                  <span className="text-sm text-green-700 font-medium">Verified</span>
                  <p className="text-xs text-green-600">Your notification settings are active and working</p>
                </div>
              </>
            ) : (
              <>
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <div>
                  <span className="text-sm text-amber-700 font-medium">Not Verified</span>
                  <p className="text-xs text-amber-600">Save your preferences, then click &quot;Verify Settings&quot; below</p>
                </div>
              </>
            )}
          </div>
          <Badge variant={isVerified ? "default" : "secondary"}>
            {isVerified ? "Active" : "Pending"}
          </Badge>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handleSavePreferences}
            disabled={saving}
            variant="outline"
            className="flex-1"
            data-testid="save-notification-preferences-btn"
          >
            {saving ? "Saving..." : "Save Preferences"}
          </Button>
          <Button
            onClick={handleTestNotification}
            disabled={testing}
            className="flex-1"
            variant={isVerified ? "outline" : "default"}
            data-testid="test-notification-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            {testing ? "Verifying..." : isVerified ? "Re-Verify Settings" : "Verify Settings"}
          </Button>
        </div>
        {!isVerified && (
          <p className="text-xs text-muted-foreground text-center">
            Clicking &quot;Verify Settings&quot; sends a test notification to confirm delivery works
          </p>
        )}

        {/* Admin Section */}
        {isAdmin && (
          <div className="pt-6 border-t">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Admin Settings
            </h4>
            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
              <div>
                <Label htmlFor="failure_notifications">Receive Failure Notifications</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Get notified when a notification fails to deliver to any user
                </p>
              </div>
              <Switch
                id="failure_notifications"
                checked={receiveFailureNotifications}
                onCheckedChange={(checked) => {
                  setReceiveFailureNotifications(checked);
                  // Auto-save admin setting
                  setTimeout(() => handleSaveAdminNotifications(), 100);
                }}
                data-testid="failure-notifications-switch"
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default NotificationSettings;
